import React, { useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { vortxTypography } from '@vortx/shared-ui';

/**
 * LogTerminal - Scrolling log display in the lower-right of the 3D viewport.
 *
 * Shows real-time backend messages (MPC solve progress, agent status, errors)
 * in a typewriter-style terminal with auto-scroll.
 *
 * @param {{ logs: string[] }} props
 */
export default function LogTerminal({ logs }) {
    const scrollRef = useRef(null);

    // Auto-scroll to bottom when new logs arrive
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [logs]);

    if (!logs || logs.length === 0) return null;

    // Show last 20 lines
    const visibleLogs = logs.slice(-20);

    return (
        <Box sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderBottom: '1px solid rgba(95, 212, 196, 0.1)',
                flexShrink: 0,
            }}>
                <Box sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: '#5fd4c4',
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                        '0%, 100%': { opacity: 0.4 },
                        '50%': { opacity: 1 },
                    },
                }} />
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.5rem',
                    fontWeight: 600,
                    color: '#5fd4c4',
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                }}>
                    Engine Log
                </Typography>
            </Box>

            {/* Log lines */}
            <Box
                ref={scrollRef}
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 1.5,
                    py: 0.5,
                    '&::-webkit-scrollbar': { width: 4 },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(95, 212, 196, 0.2)',
                        borderRadius: 2,
                    },
                }}
            >
                {visibleLogs.map((line, i) => (
                    <Typography
                        key={i}
                        sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.55rem',
                            lineHeight: 1.6,
                            color: line.includes('ERROR') || line.includes('Failed')
                                ? '#e74c3c'
                                : line.includes('Solving') || line.includes('started')
                                    ? '#5fd4c4'
                                    : line.includes('Step')
                                        ? '#b28c50'
                                        : '#888',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {line}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
}
